import React from 'react';
import { Star, GripVertical } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ratingPoints } from '../questionTypes';
import surveyApi from '../surveyApi';
import AuthImage from './AuthImage';
import { useTrainingLang } from '../../i18n/TrainingLangContext';

// Renders the answer control for one answerable question.
// Props: { surveyId, question, answer: { option_ids, value, text, ranks }, onChange(newAnswer), disabled }
export default function SurveyQuestionInput({ surveyId, question, answer, onChange, disabled = false }) {
  const { t } = useTrainingLang();
  const a = answer || { option_ids: [], value: null, text: '' };
  const type = question.question_type;
  const cfg = question.config || {};

  const setOptions = (ids) => onChange({ ...a, option_ids: ids });
  const setValue = (value) => onChange({ ...a, value });
  const setText = (text) => onChange({ ...a, text });

  // ---- choice: single (radio) ----
  if (type === 'single_choice') {
    return (
      <div className="space-y-2">
        {question.options.map((opt) => {
          const selected = a.option_ids.includes(opt.id);
          return (
            <label key={opt.id}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selected ? 'border-amber-500 bg-amber-900/20' : 'border-slate-700 hover:bg-slate-700/40'}`}>
              <input type="radio" className="accent-amber-500" name={`q_${question.id}`}
                checked={selected} disabled={disabled} onChange={() => setOptions([opt.id])} />
              <span className="text-slate-100">{opt.option_text}</span>
            </label>
          );
        })}
      </div>
    );
  }

  // ---- choice: multi (checkbox) ----
  if (type === 'multi_choice') {
    const toggle = (id) => {
      const next = a.option_ids.includes(id)
        ? a.option_ids.filter((x) => x !== id)
        : [...a.option_ids, id];
      setOptions(next);
    };
    return (
      <div className="space-y-2">
        {question.options.map((opt) => {
          const selected = a.option_ids.includes(opt.id);
          return (
            <label key={opt.id}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selected ? 'border-amber-500 bg-amber-900/20' : 'border-slate-700 hover:bg-slate-700/40'}`}>
              <input type="checkbox" className="accent-amber-500"
                checked={selected} disabled={disabled} onChange={() => toggle(opt.id)} />
              <span className="text-slate-100">{opt.option_text}</span>
            </label>
          );
        })}
        {(cfg.min_select || cfg.max_select) && (
          <p className="text-xs text-slate-400">
            {cfg.min_select ? `${t('surveySelectAtLeast', { count: cfg.min_select })} ` : ''}
            {cfg.max_select ? t('surveySelectUpTo', { count: cfg.max_select }) : ''}
          </p>
        )}
      </div>
    );
  }

  // ---- choice: dropdown ----
  if (type === 'dropdown') {
    return (
      <select
        className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100"
        value={a.option_ids[0] ?? ''} disabled={disabled}
        onChange={(e) => setOptions(e.target.value ? [Number(e.target.value)] : [])}>
        <option value="">— Select —</option>
        {question.options.map((opt) => (
          <option key={opt.id} value={opt.id}>{opt.option_text}</option>
        ))}
      </select>
    );
  }

  // ---- choice: yes / no ----
  if (type === 'yes_no') {
    return (
      <div className="flex gap-3">
        {question.options.map((opt) => {
          const selected = a.option_ids.includes(opt.id);
          return (
            <button key={opt.id} type="button" disabled={disabled}
              onClick={() => setOptions([opt.id])}
              className={`px-6 py-2 rounded-lg border font-medium transition-colors ${
                selected ? 'border-amber-500 bg-amber-600 text-white' : 'border-slate-700 text-slate-200 hover:bg-slate-700/40'}`}>
              {opt.option_text}
            </button>
          );
        })}
      </div>
    );
  }

  // ---- ranking: drag to order, top = 1 (with optional per-item images) ----
  if (type === 'ranking') {
    return (
      <RankingInput surveyId={surveyId} question={question} answer={a}
        onChange={onChange} disabled={disabled} />
    );
  }

  // ---- numeric: rating ----
  if (type === 'rating') {
    const points = ratingPoints(cfg);
    const current = a.value == null ? null : Number(a.value);
    return (
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          {points.map((p) => {
            const active = current != null && (cfg.icon === 'star' ? p <= current : p === current);
            if (cfg.icon === 'star') {
              return (
                <button key={p} type="button" disabled={disabled} onClick={() => setValue(p)}
                  className="p-1" title={`${p}`}>
                  <Star className={`w-8 h-8 ${active ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`} />
                </button>
              );
            }
            return (
              <button key={p} type="button" disabled={disabled} onClick={() => setValue(p)}
                className={`w-10 h-10 rounded-lg border font-medium ${
                  active ? 'border-amber-500 bg-amber-600 text-white' : 'border-slate-700 text-slate-200 hover:bg-slate-700/40'}`}>
                {p}
              </button>
            );
          })}
        </div>
        {(cfg.min_label || cfg.max_label) && (
          <div className="flex justify-between text-xs text-slate-400 mt-1 max-w-xs">
            <span>{cfg.min_label}</span><span>{cfg.max_label}</span>
          </div>
        )}
      </div>
    );
  }

  // ---- numeric: likert ----
  if (type === 'likert') {
    const points = Array.isArray(cfg.points) && cfg.points.length ? cfg.points : [];
    const current = a.value == null ? null : Number(a.value);
    return (
      <div className="flex flex-wrap gap-2">
        {points.map((pt) => {
          const selected = current === pt.value;
          return (
            <button key={pt.value} type="button" disabled={disabled} onClick={() => setValue(pt.value)}
              className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                selected ? 'border-amber-500 bg-amber-900/30 text-amber-100' : 'border-slate-700 text-slate-200 hover:bg-slate-700/40'}`}>
              {pt.label}
            </button>
          );
        })}
      </div>
    );
  }

  // ---- numeric: NPS 0–10 ----
  if (type === 'nps') {
    const current = a.value == null ? null : Number(a.value);
    // Full literal class strings (Tailwind purges dynamically-built names).
    const npsSelected = {
      rose: 'border-rose-500 bg-rose-600 text-white',
      amber: 'border-amber-500 bg-amber-600 text-white',
      emerald: 'border-emerald-500 bg-emerald-600 text-white',
    };
    const tintKey = (n) => (n <= 6 ? 'rose' : n <= 8 ? 'amber' : 'emerald');
    return (
      <div>
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: 11 }, (_, n) => {
            const selected = current === n;
            return (
              <button key={n} type="button" disabled={disabled} onClick={() => setValue(n)}
                className={`w-10 h-10 rounded-lg border font-medium transition-colors ${
                  selected ? npsSelected[tintKey(n)] : 'border-slate-700 text-slate-200 hover:bg-slate-700/40'}`}>
                {n}
              </button>
            );
          })}
        </div>
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>{t('surveyNpsLow')}</span><span>{t('surveyNpsHigh')}</span>
        </div>
      </div>
    );
  }

  // ---- text ----
  if (type === 'short_text') {
    return (
      <input type="text" disabled={disabled} value={a.text || ''}
        maxLength={cfg.max_length || undefined} placeholder={cfg.placeholder || ''}
        onChange={(e) => setText(e.target.value)}
        className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100" />
    );
  }
  if (type === 'long_text') {
    return (
      <textarea rows={4} disabled={disabled} value={a.text || ''}
        maxLength={cfg.max_length || undefined} placeholder={cfg.placeholder || ''}
        onChange={(e) => setText(e.target.value)}
        className="w-full px-3 py-2 rounded bg-slate-800 border border-slate-700 text-slate-100" />
    );
  }

  return null;
}

// One draggable ranking row: drag handle + position badge + optional thumbnail + label.
function RankRow({ opt, position, imageUrl, disabled }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: String(opt.id), disabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border ${
        isDragging ? 'border-amber-500 shadow-lg' : 'border-slate-700'} bg-slate-800`}>
      <button type="button" {...attributes} {...listeners} disabled={disabled}
        aria-label={`Drag to reorder ${opt.option_text}`}
        className="touch-none cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-200 disabled:cursor-not-allowed">
        <GripVertical className="w-5 h-5" />
      </button>
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-600 text-white text-sm font-semibold flex items-center justify-center">
        {position}
      </span>
      {imageUrl && (
        <AuthImage url={imageUrl} alt={opt.option_text}
          className="h-14 w-auto max-w-[45%] object-contain rounded bg-white/5" />
      )}
      <span className="text-slate-100">{opt.option_text}</span>
    </div>
  );
}

// Drag-to-order ranking control. Produces the same answer shape the rest of the
// engine expects: a.ranks = { [optionId]: position } (dense 1..N by list order).
// ranks stays EMPTY until the first drag, so a required ranking isn't counted as
// answered until the respondent actually orders the list.
function RankingInput({ surveyId, question, answer, onChange, disabled }) {
  const a = answer || {};
  const cfg = question.config || {};
  const opts = question.options || [];
  const ranks = a.ranks || {};
  const itemImages = cfg.item_images || {};

  // Display order: by assigned rank once ranked, else the option order.
  const ordered = React.useMemo(() => {
    if (!Object.keys(ranks).length) return opts;
    return [...opts].sort((x, y) => (ranks[x.id] || 999) - (ranks[y.id] || 999));
  }, [opts, ranks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const commit = (list) => {
    const next = {};
    list.forEach((opt, i) => { next[opt.id] = i + 1; });
    onChange({ ...a, ranks: next });
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const from = ordered.findIndex((o) => String(o.id) === String(active.id));
    const to = ordered.findIndex((o) => String(o.id) === String(over.id));
    if (from < 0 || to < 0) return;
    // Even a drop-in-place commits the current order (counts as answered).
    commit(from === to ? ordered : arrayMove(ordered, from, to));
  };

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]} onDragEnd={onDragEnd}>
        <SortableContext items={ordered.map((o) => String(o.id))} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {ordered.map((opt, i) => (
              <RankRow key={String(opt.id)} opt={opt} position={i + 1} disabled={disabled}
                imageUrl={surveyId && itemImages[opt.option_value]
                  ? surveyApi.surveyImageUrl(surveyId, itemImages[opt.option_value])
                  : null} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <p className="text-xs text-slate-400">
        Drag to arrange from top (1 = your top choice) to bottom. Every item gets a position.
      </p>
    </div>
  );
}
